import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import DefaultCartButton from 'components/CartButton/DefaultCartButton';
import CffpCartButton from 'components/CartButton/CffpCartButton';
import { setItem, getCartKey, getAffiliate } from 'utils/Cookies';
import hexToString from 'utils/Hex';
import { getWindowDomain, getCurrentURL, getCartDomain } from 'utils/Window';
import { isEmpty } from 'lodash';

const CartButtonContainer = (props) => {
  const {
    productId,
    orgCode,
    subType,
    title,
    rightIcon,
    leftIcon,
    callToPurchaseText,
    callToPurchasePage,
    callToPurchasePhoneNumber,
    seatCountThreshold,
    seatsRemaining,
    lastSaleDate,
    seminarStartDate,
    isForSaleRetail,
    orderingClosedText,
    preconfiguredOption,
    disabled,
    cartButtonType,
  } = props;

  // moment().startOf('date') and moment().startOf('day') not working for some reason
  const now = moment.utc()
    .hours(0)
    .minutes(0)
    .seconds(0)
    .milliseconds(0);

  const lastSaleDateMoment = lastSaleDate && moment.utc(lastSaleDate)
    .hours(0)
    .minutes(0)
    .seconds(0)
    .milliseconds(0);

  const startDateMoment = seminarStartDate && moment.utc(seminarStartDate)
    .hours(0)
    .minutes(0)
    .seconds(0)
    .milliseconds(0);

  const isPastPurchaseDate = (lastSaleDateMoment && lastSaleDateMoment < now)
    && (startDateMoment && startDateMoment < now);

  const isCallToPurchase = !isPastPurchaseDate
    && (((seatsRemaining === 0 || seatsRemaining) && seatsRemaining <= seatCountThreshold)
    || ((lastSaleDateMoment && lastSaleDateMoment < now) && (startDateMoment && lastSaleDateMoment < startDateMoment)));

  const calculatedTitle = ((!isForSaleRetail || isPastPurchaseDate) && orderingClosedText)
    || (isCallToPurchase && callToPurchaseText) || title;

  function callToPurchaseHandler(location, e) {
    if (e) {
      e.preventDefault();
    }

    window.location = location;
  }

  function defaultAddToCartHandler() {
    const { affiliate } = getAffiliate();
    const cartKey = getCartKey();
    const { number, displayName, productId: id } = preconfiguredOption || {};
    const options = !isEmpty(preconfiguredOption) &&
        {
          [number]: {
            productID: id,
            preconfigured: true,
            title: displayName,
          },
        };
    const cartValue = {
      product: {
        productID: productId,
        orgCode,
        options,
      },
      affiliate: hexToString(affiliate),
      continueShoppingHref: getCurrentURL(),
    };

    const domain = getWindowDomain();

    const cookieOptions = {
      path: '/',
      expires: 30,
      domain: domain !== 'localhost' && `.${domain}`,
    };

    setItem(cartKey, JSON.stringify(cartValue), cookieOptions);

    window.location = `//${getCartDomain()}/cart/items`;
  }

  const componentTypes = {
    Default: {
      component: DefaultCartButton,
      addToCartHandler: () => defaultAddToCartHandler(),
    },
    CFFP: {
      component: CffpCartButton,
      addToCartHandler: () => {},
    },
  };

  const getCartButton = (Component, addToCartHandler) => (
    <Component
      subType={subType}
      title={calculatedTitle}
      rightIcon={!isCallToPurchase ? rightIcon : ''}
      leftIcon={!isCallToPurchase ? leftIcon : 'far fa-phone'}
      disabled={disabled || !isForSaleRetail || isPastPurchaseDate}
      isCallToPurchase={isCallToPurchase}
      callToPurchasePage={callToPurchasePage}
      callToPurchasePhoneNumber={callToPurchasePhoneNumber}
      callToPurchaseHandler={callToPurchaseHandler}
      addToCartHandler={addToCartHandler}
      productId={productId}
      continueShoppingHref={getCurrentURL()}
    />
  );

  const componentType = componentTypes[cartButtonType];

  return getCartButton(componentType.component, componentType.addToCartHandler);
};

const PreconfiguredOptionT = PropTypes.shape({
  productId: PropTypes.number.isRequired,
  number: PropTypes.number.isRequired,
  displayName: PropTypes.string,
});

CartButtonContainer.propTypes = {
  productId: PropTypes.number.isRequired,
  orgCode: PropTypes.string,
  subType: PropTypes.string,
  title: PropTypes.string,
  rightIcon: PropTypes.string,
  leftIcon: PropTypes.string,
  callToPurchasePhoneNumber: PropTypes.string,
  callToPurchaseText: PropTypes.string,
  callToPurchasePage: PropTypes.string,
  seatCountThreshold: PropTypes.number,
  seatsRemaining: PropTypes.number,
  lastSaleDate: PropTypes.string,
  seminarStartDate: PropTypes.string,
  isForSaleRetail: PropTypes.bool,
  orderingClosedText: PropTypes.string,
  preconfiguredOption: PreconfiguredOptionT,
  disabled: PropTypes.bool,
  cartButtonType: PropTypes.string,
};

export default CartButtonContainer;
